import { useContext } from "react";
import { useHistory, useNavigate } from "react-router";
import { ACCOUNT_LINK_INITIATE, ACCOUNT_LINK_INITIATED, PLATFORM_POPUP_CLOSE, PLATFORM_POPUP_OPEN } from "../../analytics/eventConstants";
import { trackEvent } from "../../analytics/sdkAnalytics";
import { SOMETHING_WENT_WRONG, STATUS_CONNECTED, STATUS_ERROR_INFO, STATUS_FAILED, STATUS_INFO_REQUIRED } from "../../constants/URLConstants";
import { SDKInstanceState } from "../../context/SdkInstanceContext";
import { sendingAccountConnectedCallback, versionCompare } from "../sdk_utils";
import { LoaderWithStatusState } from "../../context/LoaderWithStatusContext";
import { LoaderState } from "../../context/LoaderContext";
import { subscribeOAuthResult } from "../oauthResultChannel";
import { INSTAGRAM_DIRECT_WORKPLATFORM_ID } from "../../constants/constants";

// Chrome on Android hands every programmatic navigation to instagram.com over to the Instagram
// app, which drops Business Login OAuth URLs without handling them. Everything scripted is
// closed: GET links/redirects fire verified App Links regardless of gesture, path, or fragment;
// POST stays in the browser but Instagram's server routes every POST to its error page; and
// explicit-package Chrome intents are refused (Chrome will not intent to itself) and fall back
// to the hijacked GET. The only navigations Android never gives to an app are the ones the USER
// performs through browser chrome - long-press > "Open in new tab", or pasting the URL in the
// address bar (both verified to render the OAuth page on-device) - so the popup renders an
// interstitial that channels the user into that gesture. A normal tap is intercepted and turned
// into instructions instead of a navigation, because a tapped link would bounce to the app.
// The popup stays on our origin under the connect tab's control; the OAuth completes in the
// user-opened tab and the result comes back via BroadcastChannel or the backend status poll.
function openInstagramOAuthInterstitial(popup, authorizationUrl) {
  const httpsUrl = authorizationUrl.split("#")[0];
  const doc = popup.document;
  doc.title = "Connect Instagram";
  const style = doc.createElement("style");
  style.textContent =
    "body{font-family:sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:80vh;margin:16px;text-align:center;color:#222}" +
    "a{display:block;padding:16px 28px;background:#405DE6;color:#fff;text-decoration:none;border-radius:8px;font-size:16px;margin:16px 0 8px}" +
    "button{padding:10px 20px;background:#eee;color:#222;border:0;border-radius:8px;font-size:14px;margin-top:8px}" +
    "p{margin:6px 0}.hint{color:#c0392b;font-weight:bold;min-height:20px}";
  doc.head.appendChild(style);

  const steps = doc.createElement("p");
  steps.textContent = "Touch and HOLD the button below, then choose “Open in new tab”. Log in to Instagram there and come back.";
  const link = doc.createElement("a");
  link.href = httpsUrl;
  link.textContent = "Instagram Login — press and hold";
  const hint = doc.createElement("p");
  hint.className = "hint";
  // A plain tap would navigate and get hijacked by the Instagram app - block it and instruct.
  link.addEventListener("click", (e) => {
    e.preventDefault();
    hint.textContent = "Don’t tap — touch and HOLD the button, then choose “Open in new tab”.";
  });

  const copyBtn = doc.createElement("button");
  copyBtn.textContent = "Or copy the link — then paste it in the address bar";
  copyBtn.addEventListener("click", () => {
    popup.navigator.clipboard.writeText(httpsUrl).then(
      () => { hint.textContent = "Link copied. Open a new tab, paste it in the address bar and go."; },
      () => { hint.textContent = "Could not copy - use press and hold on the button above."; }
    );
  });

  doc.body.appendChild(steps);
  doc.body.appendChild(link);
  doc.body.appendChild(hint);
  doc.body.appendChild(copyBtn);
}

function getUsernameAuthCallbackURL(url, username) {
  try {
    const urlObj = new URL(url);
    const redirectUri = urlObj.searchParams.get("redirect_uri");
    const state = urlObj.searchParams.get("state");
    const scope = urlObj.searchParams.get("scope");
    // Build a new URL with only redirect_uri, state, and scope as query params
    let newUrl = redirectUri;
    const params = [];
    if (state) params.push(`state=${encodeURIComponent(state)}`);
    if (scope) params.push(`granted_scopes=${encodeURIComponent(scope)}`);
    params.push(`code=${username}`);
    if (params.length > 0) {
      newUrl += (redirectUri.includes("?") ? "&" : "?") + params.join("&");
    }
    return newUrl;
  } catch (e) {
    // fallback: keep url as is if parsing fails
  }
  return url;
}
function getAuthDetailsFromAuthResponse(response) {
  try {
    const urlObj = new URL(response.authorization_url);
    return [urlObj.searchParams.get("otp"), response.id];
  } catch (e) {
    // fallback: return null if parsing fails
    return null;
  }
}

export const useGetRedirectAuthFlow = (workplatformId, workplatformName, sdkParams, workplatformDetails) => {
  const { sdkInstance, dispatchSdkInstContext } = SDKInstanceState();
  const { isConnectLoader, setIsConnectLoader, setWindowPopup } = LoaderWithStatusState();
  const { loader, dispatchLoaderContext } = LoaderState();
  const navigate = useNavigate();
  let timer;
  if (workplatformId) {
    // Checking for v1 version & check for redirect flow
    if (sdkParams.version === "1" || sdkParams.redirectURL !== "null") {
      return async (flow) => {
        try {
          // saving context to session Storage
          sessionStorage.setItem("sdkParams", JSON.stringify(sdkParams));
          sessionStorage.setItem("workplatformDetails", JSON.stringify(workplatformDetails));

          const queryParamStr = `?version=${sdkParams.version}&redirectURL=${sdkParams.redirectURL}&userId=${sdkParams.userId}&appName=${sdkParams.appName}&workplatformId=${workplatformId}&workplatformName=${workplatformName}`;
          const redirectURLPostOAuth = process.env.REACT_APP_REDIRECT_URL + queryParamStr;
          const response = await sdkInstance.getAuthorizations(workplatformId, redirectURLPostOAuth);
          if (Object.keys(response).length === 0) navigate(`/${SOMETHING_WENT_WRONG}`);
          else {
            let urlToOpen = response.authorization_url;
            if (flow) {
              if (flow === "phyllo_otp_flow") return getAuthDetailsFromAuthResponse(response);
              else urlToOpen = getUsernameAuthCallbackURL(urlToOpen, flow);
            }
            urlToOpen = `${process.env.REACT_APP_REDIRECT_REFERER_URL}?authURL=${urlToOpen}`;
            window.location.href = urlToOpen;
          }
          // Tracking event : ACCOUNT_LINK_INITIATE
          trackEvent(ACCOUNT_LINK_INITIATE, {
            work_platform_id: workplatformId,
            work_platform_name: workplatformName,
            account_connection_type: "New_Connection",
            oauth_url: response.authorization_url,
          });
        } catch (err) {
          console.log(err);
          throw err;
        }
      };
    }

    // Popup flow
    return async (flow) => {
      try {
        setIsConnectLoader(true);
        dispatchLoaderContext(true);

        let windowPopup = "";
        let redirectURLPostOAuth = "";
        const popupHeight = window.innerHeight / 1.33;
        const popupWidth = window.innerWidth / 1.33;
        let timer;
        if (!JSON.parse(sdkParams.mobile) && flow !== "phyllo_otp_flow") {
          windowPopup = window.open(
            "",
            "",
            `popup,resizeable,width=${popupWidth},height=${popupHeight},top=${popupHeight / 3},left=${popupWidth / 3.5}`
          );
          if (windowPopup === null || windowPopup === "undefined") {
            windowPopup = window.open("", "_blank", `top=${popupHeight / 3},left=${popupWidth / 3.5}`);
          }
          const queryParamStr = `?version=${sdkParams.version}&redirectURL=${sdkParams.redirectURL}&userId=${sdkParams.userId}&appName=${sdkParams.appName}&workplatformId=${workplatformId}&workplatformName=${workplatformName}`;
          redirectURLPostOAuth = process.env.REACT_APP_REDIRECT_URL + queryParamStr;
        } else redirectURLPostOAuth = "phyllo://auth";

        const response = await sdkInstance.getAuthorizations(workplatformId, redirectURLPostOAuth);

        if (Object.keys(response).length === 0) {
          navigate(`/${SOMETHING_WENT_WRONG}`);
          return;
        }

        let unsubscribeResult = () => {};
        let resolved = false;
        let pollTimer;
        const eventHandlerCallback = (event) => {
          // To check if no other app is interfering with Phyllo SDK callback
          dispatchLoaderContext(true);
          if (resolved || event.origin.indexOf(process.env.REACT_APP_PHYLLO_DOMAIN) === -1 || !event.data.status || !event.data.authorization_id) return;
          resolved = true;
          unsubscribeResult();
          const responseFromEvent = event.data;
          clearInterval(timer);
          clearInterval(pollTimer);
          try {
            if (windowPopup && !windowPopup.closed) windowPopup.close();
          } catch (err) {
            // popup already gone or inaccessible
          }
          if (responseFromEvent && responseFromEvent.status === "success") {
            if (responseFromEvent.account_ids) {
              //   // Sending onAccountConnected callback
              //   sendingAccountConnectedCallback(responseFromEvent.account_ids.split(","), workplatformId, sdkParams);

              navigate(`/${STATUS_CONNECTED}/${responseFromEvent.account_ids}`);
              setIsConnectLoader(false);
            } else {
              navigate(`/${SOMETHING_WENT_WRONG}`);
              setIsConnectLoader(false);
              dispatchLoaderContext(false);
            }
          } else if (responseFromEvent.status === "info_required") {
            navigate(`/${STATUS_INFO_REQUIRED}/${responseFromEvent?.authorization_id}/`);
          } else if (responseFromEvent.status === "failed_info") {
            dispatchLoaderContext(true);
            navigate(`/${STATUS_ERROR_INFO}/${responseFromEvent.authorization_id}/`, {
              state: {
                authorization_id: responseFromEvent.authorization_id,
                error_message: responseFromEvent.error_message,
                error_code: responseFromEvent.error_code,
              },
            });
            setIsConnectLoader(false);
          } else {
            setIsConnectLoader(false);
            navigate(`/${STATUS_FAILED}/${responseFromEvent.authorization_id}`);
          }
        };

        // Backend polling: result delivery that does not depend on window.opener. Instagram's
        // COOP header severs the popup handle the moment its page loads, so postMessage /
        // BroadcastChannel can miss - but the OAuth callback always reaches our backend, so the
        // authorization status is the source of truth.
        const pollAuthorizationStatus = async () => {
          if (resolved) return;
          try {
            const info = await sdkInstance.getAuthorizationInfo(response.id);
            if (resolved || !info || !info.status) return;
            if (info.status === "SUCCESS") {
              eventHandlerCallback({
                origin: window.location.origin,
                data: { status: "success", authorization_id: info.id, account_ids: (info.account_ids || []).join(",") },
              });
            } else if (["FAILED", "EXPIRED", "CANCELLED"].includes(info.status)) {
              eventHandlerCallback({
                origin: window.location.origin,
                data: { status: "failed", authorization_id: info.id },
              });
            }
            // IN_PROGRESS: keep waiting
          } catch (err) {
            console.error(`${err} occurred while polling authorization status`);
          }
        };

        let urlToOpen = response.authorization_url;
        if (flow) {
          if (flow === "phyllo_otp_flow") return getAuthDetailsFromAuthResponse(response);
          else urlToOpen = getUsernameAuthCallbackURL(urlToOpen, flow);
        }
        // Android + Instagram Direct: the OAuth URL can only be opened by a user gesture through
        // browser chrome (see openInstagramOAuthInterstitial); every programmatic navigation is
        // intercepted by the Instagram app.
        const useAndroidManualFlow = !flow && workplatformId === INSTAGRAM_DIRECT_WORKPLATFORM_ID && /android/i.test(navigator.userAgent);
        urlToOpen = `${process.env.REACT_APP_REDIRECT_REFERER_URL}?authURL=${urlToOpen}`;
        setWindowPopup(windowPopup);
        if (!JSON.parse(sdkParams.mobile)) {
          if (useAndroidManualFlow) openInstagramOAuthInterstitial(windowPopup, response.authorization_url);
          else windowPopup.location = urlToOpen;

          // Tracking event : PLATFORM_POPUP_OPEN
          trackEvent(PLATFORM_POPUP_OPEN, {
            work_platform_id: workplatformId,
            work_platform_name: workplatformName,
            popup_open_url: urlToOpen,
          });

          // The popup-close watcher treats a closed popup as an abandoned connection. That holds
          // for the desktop popup, where the OAuth lives in the popup itself, but not for the
          // Android manual flow: there the user opens the OAuth in their own tab and our
          // interstitial popup is expected to be closed or abandoned while the login is still in
          // progress. Reporting failure then also loses the race against the user tapping Allow -
          // the authorization goes terminal and the callback comes back "user authentication
          // failed". So the client never terminates the Android flow: the backend status poll
          // (and the backend's own expiry) is the only decider.
          timer = useAndroidManualFlow ? null : setInterval(() => {
            if (windowPopup.closed) {
              // Tracking event : PLATFORM_POPUP_CLOSE
              windowPopup.close();

              resolved = true;
              unsubscribeResult();
              dispatchLoaderContext(true);
              setIsConnectLoader(false);
              clearInterval(timer);
              clearInterval(pollTimer);
              navigate(`/${STATUS_FAILED}/${response.id}`);
              try {
                trackEvent(PLATFORM_POPUP_CLOSE, {
                  popup_close_url: windowPopup?.location?.href,
                  initiator: "user",
                  work_platform_id: workplatformId,
                  work_platform_name: workplatformName,
                  reason: "user has abandoned the connection flow",
                });
              } catch (err) {
                trackEvent(PLATFORM_POPUP_CLOSE, {
                  popup_close_url: "Fetching URL not supported in Safari",
                  initiator: "user",
                  work_platform_id: workplatformId,
                  work_platform_name: workplatformName,
                  reason: "user has abandoned the connection flow",
                });
              }
            }
          }, 1500);

          unsubscribeResult = subscribeOAuthResult(eventHandlerCallback);
          pollTimer = setInterval(pollAuthorizationStatus, 3000);
        } else {
          // This event listener is for web-view on mobile
          window.addEventListener("message", eventHandlerCallback, false);
          if (sdkParams.mobileVersion !== "null" && versionCompare(sdkParams.mobileVersion, "0.3.1") >= 0) {
            // Checking version and sending the workplatform details for analytics //Maintaining backward compatibility
            window.location.href = `${redirectURLPostOAuth}?oAuthId=${response.id}&workplatformId=${workplatformId}&workplatformName=${workplatformName}&oAuthURL=${urlToOpen}`;
          } else window.location.href = `${redirectURLPostOAuth}?oAuthURL=${urlToOpen}?oAuthId=${response.id}`;
        }
        // Tracking event : ACCOUNT_LINK_INITIATE
        trackEvent(ACCOUNT_LINK_INITIATE, {
          work_platform_id: workplatformId,
          work_platform_name: workplatformName,
          account_connection_type: "New_Connection",
          oauth_url: urlToOpen,
        });
      } catch (err) {
        console.log(err);
        throw err;
      }
    };
  }
};
